/**
 * +personas: pure model for the party chat frame and speech bubbles. Turns the sim's social events
 * ('chat', 'presence', 'party_leave') into chat lines, and holds the fade timing. No DOM.
 */
import type { ContentRegistry, DeepReadonly, Presence, SimEvent, UnitId, World } from '@keystone/sim';

export type ChatLineKind = 'say' | 'emote' | 'system';

export interface ChatLine {
  kind: ChatLineKind;
  unitId: UnitId | null;
  /** Speaker name (say / emote). */
  name: string;
  /** Speaker's class colour (say / emote). */
  color: string;
  text: string;
  /** +presence: channel tag shown before the line ('Zone' for shared-field chat); absent = party. */
  channel?: string;
}

/** Lines kept in the frame. */
export const CHAT_MAX_LINES = 50;
/** The frame stays fully visible this long after the last line, then fades over CHAT_FADE_MS. */
export const CHAT_IDLE_MS = 10_000;
export const CHAT_FADE_MS = 1500;
/** Faded frame opacity (lines are still faintly there, like WoW's chat). */
export const CHAT_MIN_OPACITY = 0;
/** Speech bubbles: shown this long (the last BUBBLE_FADE_MS fading), cut at BUBBLE_MAX_CHARS. */
export const BUBBLE_MS = 5000;
export const BUBBLE_FADE_MS = 600;
export const BUBBLE_MAX_CHARS = 60;

const SYSTEM_TEXT: Readonly<Record<Presence, string>> = { afk: 'is now AFK.', wandering: 'wandered off.' };

/**
 * Chat lines for one batch of events. `away` remembers each unit's last presence (so "is back"
 * only follows an AFK, and coming back from a node stays quiet); it is updated in place.
 */
export function socialLines(events: readonly SimEvent[], world: DeepReadonly<World>, content: ContentRegistry, away: Map<UnitId, Presence>): ChatLine[] {
  const out: ChatLine[] = [];
  const who = (id: UnitId): { name: string; color: string } => {
    const u = world.units[id];
    const cls = u?.classId ? content.class(u.classId) : undefined;
    return { name: u?.name ?? id, color: cls?.color ?? '#e6e9ef' };
  };
  const system = (id: UnitId, text: string): void => {
    out.push({ kind: 'system', unitId: id, name: who(id).name, color: '', text: `${who(id).name} ${text}` });
  };
  for (const e of events) {
    if (e.type === 'chat') {
      const w = who(e.sourceID);
      out.push({ kind: e.emote ? 'emote' : 'say', unitId: e.sourceID, name: w.name, color: w.color, text: e.text });
    } else if (e.type === 'presence') {
      const was = away.get(e.sourceID);
      if (e.status) {
        away.set(e.sourceID, e.status);
        system(e.sourceID, SYSTEM_TEXT[e.status]);
      } else {
        away.delete(e.sourceID);
        if (was === 'afk') system(e.sourceID, 'is back.');
      }
    } else if (e.type === 'party_leave') {
      away.delete(e.sourceID);
      system(e.sourceID, 'has left the group.');
    }
  }
  return out;
}

/** The line as plain text ("[Name]: text", "Name dances", system text as is). */
export function lineText(l: ChatLine): string {
  if (l.kind === 'say') return `[${l.name}]: ${l.text}`;
  if (l.kind === 'emote') return `${l.name} ${l.text}`;
  return l.text;
}

/** Frame opacity: full while hovered / typing or within CHAT_IDLE_MS of the last line, then fading. */
export function chatOpacity(now: number, lastLineAt: number, active: boolean): number {
  if (active) return 1;
  const idle = now - lastLineAt - CHAT_IDLE_MS;
  if (idle <= 0) return 1;
  return Math.max(CHAT_MIN_OPACITY, 1 - idle / CHAT_FADE_MS);
}

/** Bubble text, cut with an ellipsis. */
export function bubbleText(text: string, max = BUBBLE_MAX_CHARS): string {
  const t = text.trim();
  return t.length <= max ? t : `${t.slice(0, max - 1).trimEnd()}…`;
}

/** Bubble opacity at `now` (0 = expired). */
export function bubbleOpacity(now: number, since: number): number {
  const left = since + BUBBLE_MS - now;
  if (left <= 0) return 0;
  return left >= BUBBLE_FADE_MS ? 1 : left / BUBBLE_FADE_MS;
}
